import { DeleteOutlined, SearchOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm, Space, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Outlet, useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';
import { deleteUser, getUsers } from '../../api/user';
import { Page } from '../../components/page';
import { InfiniteTable } from '../../components/table';
import { useFetch } from '../../hooks';
import { generateRouteUrl } from '../../library/constants/routes.js';
import { UserColumns } from '../../library/constants/tableColumns';
import { objectToSearchParams, searchParamsToObject } from '../../library/helpers/index.js';

function Users(props) {
  const { title } = props;
  const initialData = useLoaderData();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [archivingId, setArchivingId] = useState(null);

  const [list, hasMore, isLoading, searchParamsParsed, {
    setSort,
    setFilters,
    loadMore
  }] = useFetch({ initialData });

  const handleArchive = async (userId) => {
    try {
      setArchivingId(userId);
      await deleteUser(userId);
      message.success('User archived successfully');
      revalidator.revalidate();
    } catch (error) {
      message.error(error.message || 'Failed to archive user');
    } finally {
      setArchivingId(null);
    }
  };

  const columns = useMemo(() => [
    ...UserColumns,
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title="Archive User"
            description={`Are you sure you want to archive "${record.name || record.email}"?`}
            onConfirm={(e) => {
              e?.stopPropagation?.();
              handleArchive(record.id);
            }}
            onCancel={(e) => e?.stopPropagation?.()}
            okText="Archive"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: archivingId === record.id }}
          >
            <Tooltip title="Archive user">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={archivingId === record.id}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ], [archivingId]);

  return (
    <Page className='users' title={title} header={(
      <>
        <div></div>
        <Space.Compact style={{ width: '100%', maxWidth: '400px' }}>
          <Input.Search placeholder="Search" defaultValue={searchParamsParsed?.filter?.search ?? ''} prefix={<SearchOutlined />} onChange={(e) => setFilters({ 'search': e.target.value })} allowClear />
        </Space.Compact>
        <Button type="primary" onClick={() => { navigate(generateRouteUrl('UserCreate')) }} icon={<UserAddOutlined />}>Add new User</Button>
      </>
    )} >

      <InfiniteTable
        columns={columns}
        data={list}
        rowKey="id"
        onRow={user => ({
          onClick: () => {
            navigate(`${generateRouteUrl('UserDetails', { userId: user.id })}?${objectToSearchParams(searchParamsParsed)}`);
          }
        })}
        isLoading={isLoading}
        onChange={setSort}
        hasMore={hasMore}
        loadMore={loadMore}
        sticky
      />
      <Outlet />
    </Page>
  );
}

Users.Loader = async ({ request }) => {
  const url = new URL(request.url)
  const { filter, page, sort } = searchParamsToObject(url.searchParams)
  const { data, meta } = await getUsers({ filter, page, ...sort })
  return { data, meta }
}

Users.propTypes = {
  title: PropTypes.string,
}

export default Users
